import {
  IonCard,
  IonCardContent,
  IonCol,
  IonRow,
  IonText,
} from "@ionic/react";
import React from "react";
import Content from "../components/Content";
import { CURRENT_PRIVACY_POLICY_VERSION } from "../models/User";
import { usePageTitle } from "../util/sugar";

/**
 * Canonical Privacy Policy & Terms of Service for Song Share.
 *
 * Linked from the first-login PrivacyConsentModal and from the footer of
 * Account settings. The version string at the top must match
 * CURRENT_PRIVACY_POLICY_VERSION in src/models/User.ts — when this policy
 * changes materially, bump the version constant so previously-consented
 * users are re-prompted.
 *
 * IMPORTANT: This text is a starting draft. Before treating it as your
 * institution's official policy, have it reviewed by:
 *   - Your institution's legal counsel
 *   - Your institution's FERPA officer (if used for student work)
 *   - Your accessibility / compliance reviewer
 */
const Privacy: React.FC = () => {
  usePageTitle("Privacy Policy");

  return (
    <Content>
      <IonRow>
        <IonCol sizeMd="10" sizeLg="8" offsetMd="1" offsetLg="2">
          <IonCard>
            <IonCardContent>
              <IonText>
                <h1>Privacy Policy & Terms of Service</h1>
                <p>
                  <em>
                    Effective version: {CURRENT_PRIVACY_POLICY_VERSION}
                  </em>
                </p>

                <p>
                  Song Share is a music-sharing platform built to let
                  songwriters, students, and listeners share, comment on, and
                  discover original music. This document explains what
                  information we collect, how we use it, and the choices you
                  have about its visibility — including when your work may be
                  embedded on websites outside of Song Share.
                </p>

                <p>
                  Please read it carefully. By creating an account, signing
                  in, or continuing to use Song Share you agree to the terms
                  described here. If you don't agree, please don't use the
                  service.
                </p>

                <h2>1. What we collect</h2>
                <p>When you use Song Share, we collect and store:</p>
                <ul>
                  <li>
                    <strong>Account information.</strong> The email address
                    you sign in with, an alias (display name) you choose, an
                    avatar image if you upload one, your region if you set
                    one, an optional artist statement, and a website link if
                    you provide one.
                  </li>
                  <li>
                    <strong>Songs, albums, and playlists.</strong> Audio or
                    video files you upload, song titles, descriptions, cover
                    art, and the lists / collections you assemble.
                  </li>
                  <li>
                    <strong>Comments and messages.</strong> Comments you post
                    on songs and private messages you send through the
                    Conversations feature.
                  </li>
                  <li>
                    <strong>Activity data.</strong> Plays, favorites, and
                    follows you generate; basic typing-indicator presence in
                    Conversations.
                  </li>
                  <li>
                    <strong>Technical data.</strong> Standard web logs and
                    error reports from our hosting and analytics providers.
                  </li>
                </ul>

                <h2>2. Your privacy setting — Public or Members Only</h2>
                <p>
                  When you first sign in to Song Share, you'll be asked to
                  choose a default privacy posture for your account:
                </p>
                <ul>
                  <li>
                    <strong>Public.</strong> Your songs, profile information,
                    and comments are visible to anyone visiting Song Share,
                    including people who are not logged in. Your public songs
                    may also be embedded on third-party websites such as
                    blogs, classroom sites, portfolios, or social media (see
                    Section 3).
                  </li>
                  <li>
                    <strong>Members Only.</strong> Your songs, profile, and
                    comments are visible only to other logged-in Song Share
                    members. Your work cannot be embedded on third-party
                    websites. Members-Only is the more private of the two
                    options.
                  </li>
                </ul>
                <p>
                  Whichever you choose, we record the choice along with the
                  version of this policy you agreed to and the date you
                  agreed. You can change your privacy setting at any time
                  from your Account settings, and the change takes effect
                  immediately.
                </p>
                <p>
                  If we change this policy in a way that affects your
                  choices, we will ask you to review and re-confirm your
                  setting the next time you sign in.
                </p>

                <h2>3. Embedding</h2>
                <p>
                  If your account is set to <strong>Public</strong>, your
                  songs may be embedded by anyone on websites outside Song
                  Share. An embed shows a small player with the song's title,
                  the artist's alias, cover art, and a link back to Song
                  Share. Embeds may also include comments left by other
                  public users — see Section 4.
                </p>
                <p>
                  An embedded player loads from Song Share each time it
                  appears, which means that:
                </p>
                <ul>
                  <li>
                    If you switch from Public to Members Only, your embedded
                    songs will stop playing on third-party sites going
                    forward and will display a "this content is private"
                    placeholder.
                  </li>
                  <li>
                    If you delete a song, embeds of that song will display a
                    "this content is unavailable" placeholder.
                  </li>
                  <li>
                    Edits you make to song titles, descriptions, or cover art
                    will be reflected in existing embeds.
                  </li>
                </ul>
                <p>
                  Members-Only songs are never embeddable. An embedded player
                  pointing at a Members-Only song will display a placeholder
                  asking the viewer to log in to Song Share.
                </p>

                <h2>4. Comments in embeds</h2>
                <p>
                  When a Public song is embedded on a third-party website,
                  comments may be shown alongside the player. We filter
                  comments by the commenter's own privacy setting:
                </p>
                <ul>
                  <li>
                    Comments left by users whose accounts are set to{" "}
                    <strong>Public</strong> may appear in embeds.
                  </li>
                  <li>
                    Comments left by users whose accounts are set to{" "}
                    <strong>Members Only</strong> do not appear in embeds,
                    even if the song itself is public.
                  </li>
                </ul>
                <p>
                  If you switch from Public to Members Only, your past
                  comments will stop appearing in embeds.
                </p>

                <h2>5. How we share information</h2>
                <p>
                  Song Share uses third-party services to operate. Your data
                  is processed by:
                </p>
                <ul>
                  <li>
                    <strong>Google Firebase</strong> (authentication,
                    database, hosting, analytics).
                  </li>
                  <li>
                    <strong>Algolia</strong> (powering search for songs and
                    artists).
                  </li>
                  <li>
                    <strong>Amazon S3</strong> (storing uploaded audio and
                    images).
                  </li>
                </ul>
                <p>
                  These providers process data on our behalf under their own
                  privacy and security commitments. We do not sell your
                  personal information, and we do not share it with
                  advertisers.
                </p>

                <h2>6. Your rights</h2>
                <p>You can, at any time:</p>
                <ul>
                  <li>
                    Change your privacy setting in Account settings (Public ↔
                    Members Only).
                  </li>
                  <li>
                    Edit or delete songs you've uploaded, including their
                    titles, descriptions, and cover art.
                  </li>
                  <li>Delete comments you've posted.</li>
                  <li>
                    Delete your account yourself at any time from{" "}
                    <strong>Account → Other Settings</strong>. Deletion
                    removes your profile, your songs, your comments, and
                    your activity from Song Share. Embeds of your deleted
                    songs on third-party sites will return a "this content
                    is unavailable" placeholder. If you run into trouble
                    deleting your account from the interface, you can also
                    contact us (see Section 10).
                  </li>
                </ul>

                <h2>7. Information for students and educators (FERPA)</h2>
                <p>
                  If you are a student using Song Share as part of a class,
                  songs and other materials you upload may be considered an
                  "education record" under the U.S. Family Educational Rights
                  and Privacy Act (FERPA). FERPA gives you the right to
                  control disclosure of those records.
                </p>
                <p>
                  Your <strong>Members Only</strong> setting is the primary
                  mechanism Song Share offers for keeping your work out of
                  public view, including out of third-party embeds. If you
                  set your account to Members Only:
                </p>
                <ul>
                  <li>
                    Your songs, profile, and comments are not shown to
                    unauthenticated visitors in the Song Share interface.
                  </li>
                  <li>
                    Your songs cannot be embedded on third-party websites.
                  </li>
                  <li>
                    Your comments do not appear in embeds of other people's
                    songs.
                  </li>
                </ul>

                <h2>8. Security</h2>
                <p>
                  We use industry-standard practices — encrypted connections,
                  authenticated database access, audited cloud providers — to
                  protect your information. No method of transmission or
                  storage is perfectly secure. By using Song Share you
                  acknowledge that you upload content at your own risk.
                </p>

                <h2>9. Changes to this policy</h2>
                <p>
                  We may update this policy from time to time. When we make a
                  material change — for example, changing how data is shared,
                  adding new categories of disclosure, or changing the
                  meaning of the privacy settings — we will bump the policy
                  version, and the next time you sign in we will ask you to
                  review the updated policy and re-confirm your privacy
                  choice before continuing.
                </p>

                <h2>10. Contact</h2>
                <p>
                  Questions about this policy, requests for account deletion,
                  FERPA concerns, or any other privacy issue can be sent to
                  the support address listed in the Song Share Mission page.
                  We aim to respond within five business days.
                </p>

                <p>
                  <em>
                    This document is the canonical Song Share privacy
                    policy. Site administrators may publish additional
                    institution-specific privacy guidance, but those
                    documents do not override the terms above.
                  </em>
                </p>
              </IonText>
            </IonCardContent>
          </IonCard>
        </IonCol>
      </IonRow>
    </Content>
  );
};

export default Privacy;
